/**
 * The AI assistant's read side — who talked to it, the ledger, one transcript.
 *
 *   GET /v1/communities/{id}/ai/askers              who talked to the assistant
 *   GET /v1/communities/{id}/ai/conversations       the ledger / one merged stream
 *   GET /v1/communities/{id}/ai/conversations/{id}  one transcript
 *
 * WAS `questions.ts`, and held the mined-questions half too: `GET …/questions`, its
 * detail route, and the two `…/insights` blocks the "Questions & gaps" page read. That
 * page and the clustering pipeline behind it are gone — see `@api/v1/digests`, which
 * replaced them — so what is left is the AI ledger, and the file is named for it.
 *
 * §5.5 is why the conversation shapes look the way they do: `GET /admin/conversations`
 * defaulted to `limit=500` and hydrated every conversation with every message AND the
 * citation list of every message, so the browser could group, count, filter and
 * gap-annotate. Here the counts come back computed, the transcripts are a separate
 * request for the one member being read, and the 1-hour gap markers are
 * `gap_before_hours` on the message — a threshold the server owns now.
 *
 * Timestamps are UTC-aware instants with a `+00:00` offset; parse them with `parseTs`.
 */

import { apiGet } from './http';
import { community } from './envelope';
import type { CommunityRef, ListEnvelope } from './envelope';
import type { AiInsights, MemberRef } from './shapes';
import { fetchInsights } from './insights';

// ---------------------------------------------------------------------------
// The AI assistant: askers, the ledger, and one transcript
// ---------------------------------------------------------------------------

/** A citation on one answer, in citation order (position N ↔ the answer's `[N]`). */
export interface MessageSource {
  page_title: string | null;
  course_title: string | null;
}

/** One turn of a conversation. */
export interface MessageItem {
  id: number;
  role: 'user' | 'assistant';
  content: string | null;
  created_at: string | null;
  tool_rounds: number | null;
  sources: MessageSource[];
  feedback: { rating: 'up' | 'down'; comment: string | null } | null;
  /**
   * How long the silence before this message was, in hours, where it exceeded the
   * server's threshold — null otherwise. This is §5.5 moving a display rule off the
   * client: the Conversations page used to merge an asker's conversations and insert
   * a divider wherever more than an hour had passed, with the threshold living in a
   * Svelte template. The threshold is the server's now and the field is the hours,
   * so the client renders rather than decides.
   */
  gap_before_hours: number | null;
}

/**
 * One conversation. The same schema serves the ledger row, the merged stream and the
 * single transcript — `messages` is present only with `?include=messages` on the
 * list, and always on the detail route.
 */
export interface ConversationItem {
  id: number;
  /** The community member we resolved the asker to, or null for an anonymous one. */
  asker: MemberRef | null;
  asker_key: string;
  asker_name: string | null;
  asker_handle: string | null;
  /** The first thing the member asked, truncated — the ledger row's title. */
  title: string | null;
  turns: number;
  first_at: string | null;
  last_at: string | null;
  sourced: boolean;
  rating: 'up' | 'down' | null;
  messages?: MessageItem[];
}

export interface ConversationsPage extends ListEnvelope<ConversationItem> {
  community: CommunityRef;
}

/** One person who talked to the assistant, with their volume already counted. */
export interface AskerItem {
  /** The grouping key — the Skool external id, or `anon:<client_id>`. Pass it back
   *  as `?asker=` to read that person's stream. */
  key: string;
  member: MemberRef | null;
  external_id: string | null;
  handle: string | null;
  name: string | null;
  conversations: number;
  /** Member turns — questions asked, not total messages. */
  questions: number;
  last_message_at: string | null;
  /** Asked in two or more distinct weeks. */
  returning: boolean;
  /** The lessons the assistant cited for this person, most-cited first (top 8). */
  sources: { page_title: string; citations: number }[];
}

export interface AskersPage extends ListEnvelope<AskerItem> {
  community: CommunityRef;
}

/** One page of asker summaries, most recently active first. Keyset-paged: pass the
 *  previous page's `next_cursor` back as `cursor`, never construct one. */
export function fetchAskers(
  skoolId: number,
  opts: { limit?: number; cursor?: string | null } = {},
): Promise<AskersPage> {
  return apiGet<AskersPage>(community(skoolId, '/ai/askers'), {
    limit: opts.limit,
    cursor: opts.cursor,
  });
}

/** The conversation ledger, most recently active first. `asker` narrows to one
 *  person; `withMessages` returns their merged, gap-annotated stream. */
export function fetchConversations(
  skoolId: number,
  opts: { limit?: number; cursor?: string | null; asker?: string; withMessages?: boolean } = {},
): Promise<ConversationsPage> {
  return apiGet<ConversationsPage>(community(skoolId, '/ai/conversations'), {
    limit: opts.limit,
    cursor: opts.cursor,
    asker: opts.asker,
    ...(opts.withMessages ? { include: ['messages'] } : {}),
  });
}

/** One transcript — the ledger row expansion. `messages` is always present. */
export function fetchConversation(
  skoolId: number,
  conversationId: number,
): Promise<ConversationItem> {
  return apiGet<ConversationItem>(community(skoolId, `/ai/conversations/${conversationId}`));
}


// ---------------------------------------------------------------------------
// The AI Insights page
// ---------------------------------------------------------------------------

/** The blocks `AdminAIInsights.svelte` renders. `ai_headline` is included even though
 *  the page reads no field off it: `AiInsights.headline` is non-optional, and one cheap
 *  aggregate is better than a type carrying a lie. */
export const AI_BLOCKS = [
  'ai_headline',
  'ai_weekly',
  'ai_sources',
  'ai_feedback',
  'ai_askers',
  'ai_ungrounded',
] as const;

/**
 * The AI Insights page — the last legacy analytics read in the app.
 *
 * `GET /admin/analytics/ai` was TWO responses welded together (doc 04 §3.3 row 9): an
 * aggregate half and a ledger half. v1 splits them along the seam — the aggregates are
 * `?include=ai_*` blocks on the insights route, the ledger is the `ai/conversations`
 * sub-resource — so this issues both IN PARALLEL and stitches the legacy shape back
 * together for the page's existing markup.
 *
 * WHY THE LEDGER IS CAPPED. The legacy route returned every conversation ever, inline.
 * The ledger is now keyset-paginated, and this asks for one page: the page renders a
 * preview table and loads a transcript on expand (`fetchConversation`), so a second
 * page is a scroll the component does not yet do. That is a deliberate cap, stated
 * here rather than left to look like completeness.
 */
export async function fetchAiInsights(skoolId: number): Promise<AiInsights> {
  const [b, ledger] = await Promise.all([
    fetchInsights(skoolId, AI_BLOCKS, { last: 'all' }),
    fetchConversations(skoolId, { limit: 200 }),
  ]);
  return {
    last_updated: b.last_updated,
    headline: b.ai_headline,
    weekly: b.ai_weekly,
    // §2.1 nests the resolved member; the legacy asker flattened it and fell back to
    // what the chat widget recorded when we could not resolve one.
    top_askers: b.ai_askers.map((a) => ({
      member_id: a.member?.member_id ?? null,
      external_id: a.member?.external_id ?? a.external_id ?? null,
      handle: a.member?.handle ?? a.handle ?? null,
      name: a.member?.name ?? a.name ?? 'Anonymous',
      picture_profile: a.member?.picture_profile ?? null,
      questions: a.questions,
      conversations: a.conversations,
      returning: a.returning,
    })),
    top_sources: b.ai_sources,
    feedback_comments: b.ai_feedback,
    // Not rendered by this page; the block exists on the route but asking for it would
    // be a scan nobody reads.
    recent_questions: [],
    conversations: ledger.items.map((c) => ({
      id: c.id,
      asker: {
        member_id: c.asker?.member_id ?? null,
        external_id: c.asker?.external_id ?? null,
        handle: c.asker?.handle ?? c.asker_handle ?? null,
        name: c.asker?.name ?? c.asker_name ?? 'Anonymous',
        picture_profile: c.asker?.picture_profile ?? null,
      },
      title: c.title ?? '',
      turns: c.turns,
      first_at: c.first_at,
      last_at: c.last_at,
      sourced: c.sourced,
      rating: c.rating,
    })),
    ungrounded_questions: b.ai_ungrounded.map((q) => ({
      text: q.text,
      created_at: q.created_at,
      asker: q.asker_name ?? 'Anonymous',
      conversation_id: q.conversation_id,
    })),
  };
}
